/* Admin · Account 360 drawer
 *
 * Tabbed view that surfaces EVERY setting a buyer or seller has saved
 * across their Settings panels. Used by /admin/users so an admin can audit
 * any account without bouncing across tabs.
 *
 * Data source: GET /api/admin/users/{user_id}/account-360 (hydrated payload).
 * The drawer renders only what's relevant for the user's role — sellers get
 * extra tabs (Branding · Operations · Public profile · Payout schedule).
 */
import { useEffect, useState, useMemo } from "react";
import api from "../lib/api";
import { Sheet, SheetContent, SheetCloseX, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import AdminKycPanel from "./AdminKycPanel";
import { useAuth } from "../context/AuthContext";
import {
  Loader2, Mail, Phone, Briefcase, Building2, Hash, Calendar,
  User as UserIcon, ShieldCheck, ShieldAlert, ShieldQuestion, Clock,
  CheckCircle2, XCircle, MapPin, CreditCard, Users, Bell, Palette,
  Globe, Truck, Handshake, FileText, Smartphone, KeyRound, ScrollText,
  Store, Sparkles, ExternalLink, Settings2, Wallet, ArrowDownRight, ArrowUpRight, CalendarClock,
  Landmark, Receipt, DollarSign, Link2, Wrench, Activity, ShieldX,
  Trash2, AlertTriangle, AlertCircle, Stethoscope, Lock, LogIn, RefreshCw,
} from "lucide-react";
import { AdminUserControls, TenantInvitesPanel } from "./AdminControlsPanel";
import { MaskedField } from "./MaskedField";

function money(n) {
  if (n === null || n === undefined) return "—";
  if (typeof n === "object") n = n.amount ?? n.value ?? null;
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  const v = Number(n);
  const abs = Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
  return v < 0 ? `-$${abs}` : `$${abs}`;
}

// ── Reusable bits ───────────────────────────────────────────────────────
function KvRow({ label, value, mono = false, testid }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-baseline gap-3 py-1.5 border-b border-border/40 last:border-b-0" data-testid={testid}>
      <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 w-36 shrink-0">{label}</div>
      <div className={`text-[13px] ${mono ? "mono" : ""} text-foreground/85 break-words flex-1`}>{value}</div>
    </div>
  );
}

function Chip({ tone = "neutral", children }) {
  const tones = {
    neutral: "bg-secondary text-foreground/70 border-border",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  return <span className={`inline-flex items-center gap-1 chip border text-[10.5px] ${tones[tone]}`}>{children}</span>;
}

function Empty({ icon: Icon, label }) {
  return (
    <div className="py-8 text-center text-[12.5px] text-foreground/50 flex flex-col items-center gap-1.5">
      <Icon className="w-5 h-5 opacity-40"/>
      {label}
    </div>
  );
}

function SectionTitle({ icon: Icon, title, hint }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-foreground/55"/>}
        <h3 className="text-[12.5px] font-semibold tracking-tight">{title}</h3>
      </div>
      {hint && <div className="text-[11.5px] text-foreground/55 mt-0.5">{hint}</div>}
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const KYC_TONES = {
  verified: { Icon: ShieldCheck,    cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Verified" },
  pending:  { Icon: Clock,          cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "Pending" },
  rejected: { Icon: ShieldAlert,    cls: "bg-rose-50 text-rose-700 border-rose-200",          label: "Rejected" },
  none:     { Icon: ShieldQuestion, cls: "bg-secondary text-foreground/55 border-border",     label: "Not submitted" },
};

// ── Tabs ────────────────────────────────────────────────────────────────
function ProfileTab({ data, onReviewKyc, busy, note, setNote }) {
  const u = data.user;
  const kyc = u.owner_kyc;
  const kycKey = kyc?.verification_status || (kyc ? "pending" : "none");
  const tone = KYC_TONES[kycKey];
  const addr = u.address || {};
  return (
    <div className="space-y-5">
      <div className="rounded-xl bg-secondary/40 border border-border p-4 space-y-1.5">
        <KvRow label="Name"      value={u.name || "—"} testid="acc-360-name"/>
        <KvRow label="Email"     value={u.email}       testid="acc-360-email"/>
        <div data-testid="acc-360-phone-row" className="flex items-center gap-2 text-sm">
          <span className="text-stone-500 text-xs uppercase tracking-wide w-24 shrink-0">Phone</span>
          <MaskedField value={u.phone || "—"} kind="user.phone" targetId={u.id}
                       testid="acc-360-phone-masked" />
        </div>
        <KvRow label="Job title" value={u.job_title || "—"} testid="acc-360-job"/>
        <KvRow label="Company"   value={u.company || "—"} testid="acc-360-company"/>
        <div data-testid="acc-360-ein-row" className="flex items-center gap-2 text-sm">
          <span className="text-stone-500 text-xs uppercase tracking-wide w-24 shrink-0">EIN</span>
          <MaskedField value={u.ein || "—"} kind="user.ein" targetId={u.id}
                       testid="acc-360-ein-masked" />
        </div>
        <KvRow label="Business type" value={u.business_type || "—"} testid="acc-360-biz-type"/>
        <KvRow label="Website"   value={u.website || "—"} mono testid="acc-360-website"/>
        <div data-testid="acc-360-address-row" className="flex items-center gap-2 text-sm">
          <span className="text-stone-500 text-xs uppercase tracking-wide w-24 shrink-0">Address</span>
          <span className="flex-1">
            <MaskedField
              value={addr.line1 || "—"}
              kind="user.address_line1"
              targetId={u.id}
              testid="acc-360-address-masked"
            />
            {(addr.city || addr.state || addr.postal_code || addr.country) && (
              <span className="text-stone-600 ml-1">
                , {[addr.city, addr.state, addr.postal_code, addr.country].filter(Boolean).join(", ")}
              </span>
            )}
          </span>
        </div>
        <KvRow label="Role"      value={(u.role || "").toUpperCase()}/>
        <KvRow label="Joined"    value={fmtDate(u.created_at)}/>
        <KvRow label="Tenant ID" value={u.tenant_id || "—"} mono/>
      </div>

      <div className="rounded-xl bg-white border border-border p-4" data-testid="acc-360-kyc">
        <div className="flex items-center justify-between mb-2.5">
          <SectionTitle icon={ShieldCheck} title="Owner identity verification (KYC)"/>
          <span className={`chip border text-[10.5px] inline-flex items-center gap-1 ${tone.cls}`}>
            <tone.Icon className="w-3 h-3"/>{tone.label}
          </span>
        </div>
        {!kyc ? (
          <p className="text-[12px] text-foreground/55 leading-relaxed">
            This user signed up before identity verification was required.
            They'll be prompted to complete it before their next bank link or wallet activity.
          </p>
        ) : (
          <>
            <div className="space-y-1.5">
              <KvRow label="Legal name"  value={kyc.legal_name || "—"}/>
              <KvRow label="Date of birth" value={kyc.dob || "—"} mono/>
              <KvRow label="SSN" value="•••• (last 4 — hashed at rest)" mono/>
              <KvRow label="Consent recorded" value={fmtDate(kyc.consent_at)}/>
              {kyc.reviewed_at && (
                <>
                  <KvRow label="Last reviewed" value={fmtDate(kyc.reviewed_at)}/>
                  <KvRow label="Reviewer" value={kyc.reviewed_by_email || kyc.reviewed_by}/>
                </>
              )}
              {kyc.review_note && <KvRow label="Note" value={kyc.review_note}/>}
            </div>
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 mb-1.5">Reviewer note (optional)</div>
              <Input value={note} onChange={e => setNote(e.target.value)}
                placeholder="e.g. ID matched against state records"
                className="h-9 text-[12.5px]" data-testid="acc-360-kyc-note"/>
              <div className="mt-2.5 grid grid-cols-2 gap-2">
                <Button onClick={() => onReviewKyc("verified")} disabled={busy}
                  data-testid="acc-360-kyc-verify"
                  className="rounded-full h-9 bg-emerald-600 text-white hover:bg-emerald-700">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5"/>Mark verified
                </Button>
                <Button onClick={() => onReviewKyc("rejected")} disabled={busy}
                  data-testid="acc-360-kyc-reject"
                  variant="outline"
                  className="rounded-full h-9 border-rose-200 text-rose-700 hover:bg-rose-50">
                  <XCircle className="w-3.5 h-3.5 mr-1.5"/>Reject
                </Button>
              </div>
              {kycKey !== "pending" && (
                <Button onClick={() => onReviewKyc("pending")} disabled={busy}
                  data-testid="acc-360-kyc-reset"
                  variant="ghost" className="mt-1.5 w-full h-8 text-[11.5px] text-foreground/55">
                  Reset to pending
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function BusinessTab({ data }) {
  const { token } = useAuth();
  const docs = [...(data.kyb_files || []), ...(data.kyb_docs || [])];
  const filesByType = {};
  (data.kyb_files || []).forEach(f => {
    (filesByType[f.doc_type] = filesByType[f.doc_type] || []).push(f);
  });
  const DOC_LABELS = {
    ein_letter: "EIN letter (CP-575)",
    w9: "W-9 form",
    articles: "Articles of incorporation",
    bank_statement: "Bank statements",
    logo: "Logo",
  };
  return (
    <div className="space-y-5">
      {/* Iter 155 · Full admin KYC console — mirror of every wyzia_kyc field,
          account details, audit timeline, and approve/reject/reset/enforce. */}
      <SectionTitle icon={ShieldCheck} title="Identity & bank verification"
        hint="Full mirror of the verification rail. Admin can enforce, reset, approve, or reject."/>
      {data.user?.id && token ? (
        <AdminKycPanel userId={data.user.id} token={token}/>
      ) : (
        <Empty icon={ShieldCheck} label="Verification not started yet. Will auto-fire when Middesk KYB completes."/>
      )}

      <SectionTitle icon={FileText} title="KYB document uploads"
        hint="Files the user has uploaded from Settings → Business details. Soft-deleted versions are hidden."/>
      {docs.length === 0 ? (
        <Empty icon={FileText} label="No KYB documents uploaded yet."/>
      ) : (
        <div className="rounded-xl border border-border bg-white divide-y divide-border" data-testid="acc-360-kyb-list">
          {Object.entries(filesByType).map(([dt, fs]) => (
            <div key={dt} className="px-4 py-3" data-testid={`acc-360-kyb-${dt}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[12.5px] font-medium">{DOC_LABELS[dt] || dt}</div>
                <Chip tone="emerald">{fs.length} file{fs.length === 1 ? "" : "s"}</Chip>
              </div>
              <ul className="space-y-1">
                {fs.map(f => (
                  <li key={f.id} className="text-[11.5px] text-foreground/60 flex items-center gap-2">
                    <FileText className="w-3 h-3 text-foreground/40"/>
                    <span className="truncate">{f.original_filename}</span>
                    <span className="ml-auto text-foreground/40">{Math.round((f.size || 0) / 1024)} KB</span>
                    <span className="text-foreground/40">{fmtDate(f.created_at).split(",")[0]}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {(data.kyb_docs || []).length > 0 && (
            <div className="px-4 py-3" data-testid="acc-360-kyb-legacy">
              <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 mb-1">Legacy paste-URL entries</div>
              {(data.kyb_docs || []).map(d => (
                <KvRow key={d.id} label={d.doc_type} value={`${d.status} · ${fmtDate(d.created_at)}`}/>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddressesTab({ data }) {
  const addrs = data.addresses || [];
  return (
    <div className="space-y-3">
      <SectionTitle icon={MapPin} title={data.user.role === "seller" ? "Warehouse addresses" : "Shipping addresses"}
        hint={`${addrs.length} saved · cap of 5 per account`}/>
      {addrs.length === 0 ? (
        <Empty icon={MapPin} label="No addresses saved."/>
      ) : (
        <ul className="rounded-xl border border-border bg-white divide-y divide-border" data-testid="acc-360-addr-list">
          {addrs.map((a, i) => (
            <li key={a.id} className="px-4 py-3" data-testid={`acc-360-addr-${a.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <div className="text-[13px] font-medium">{a.label || `Address ${i + 1}`}</div>
                {i === 0 && <Chip tone="indigo">Default</Chip>}
                {a.is_default && i !== 0 && <Chip tone="indigo">Default</Chip>}
              </div>
              <div className="text-[12px] text-foreground/65">
                {[a.line1, a.city, a.state, a.zip, a.country].filter(Boolean).join(", ")}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PaymentMethodsTab({ data }) {
  const pms = data.payment_methods || [];
  return (
    <div className="space-y-3">
      <SectionTitle icon={CreditCard} title="Payment methods"
        hint={`${pms.length} saved · raw account/card details are stripped server-side`}/>
      {pms.length === 0 ? (
        <Empty icon={CreditCard} label="No payment methods saved."/>
      ) : (
        <ul className="rounded-xl border border-border bg-white divide-y divide-border" data-testid="acc-360-pm-list">
          {pms.map(pm => (
            <li key={pm.id} className="px-4 py-3" data-testid={`acc-360-pm-${pm.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-md bg-secondary grid place-items-center shrink-0">
                  <CreditCard className="w-4 h-4 text-foreground/55"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[13px] font-medium">{pm.label || pm.bank_name || pm.brand || "Payment method"}</div>
                    {pm.is_default && <Chip tone="indigo">Default</Chip>}
                    {pm.verified_via && <Chip tone="emerald">{pm.verified_via}</Chip>}
                    {pm.kind && <Chip>{pm.kind}</Chip>}
                  </div>
                  <div className="text-[11.5px] text-foreground/55 mt-0.5 mono">
                    {pm.kind === "card" ? `${pm.brand || "card"} ····${pm.last4 || "—"}` : `····${pm.last4 || pm.account_last4 || "—"}`}
                    {pm.rails && ` · ${pm.rails}`}
                  </div>
                  {pm.created_at && (
                    <div className="text-[11px] text-foreground/45 mt-0.5">Added {fmtDate(pm.created_at)}</div>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SellerSettingsTab({ data }) {
  const t = data.tenant || {};
  const s = t.settings || {};
  const p = s.profile || {};
  const social = p.social || {};
  const branding = t.branding || {};
  const lastFour = (v) => (v == null || v === "" ? "—" : String(v));
  return (
    <div className="space-y-5">
      {/* Branding */}
      <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-branding">
        <SectionTitle icon={Palette} title="Branding"/>
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-md border border-border bg-white grid place-items-center overflow-hidden">
            {branding.logo_url
              ? <img src={branding.logo_url} alt="" className="w-full h-full object-contain"/>
              : <span className="text-[10px] text-foreground/40">—</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-[11.5px]">
              <span className="w-3.5 h-3.5 rounded border border-foreground/15" style={{ background: branding.primary_color || "#0a1f1c" }}/>
              <code className="mono text-foreground/65">{branding.primary_color || "—"}</code>
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11.5px]">
              <span className="w-3.5 h-3.5 rounded border border-foreground/15" style={{ background: branding.accent_color || "#e8ff3a" }}/>
              <code className="mono text-foreground/65">{branding.accent_color || "—"}</code>
            </span>
          </div>
        </div>
        <KvRow label="Tenant name" value={t.name || "—"}/>
        <KvRow label="Hero title" value={branding.hero_title}/>
        <KvRow label="Hero subtitle" value={branding.hero_subtitle}/>
      </div>

      {/* Default terms + Operations */}
      <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-ops">
        <SectionTitle icon={Settings2} title="Operations & default terms"/>
        <KvRow label="Default terms" value={s.default_terms || "—"}/>
        <KvRow label="Default extra credit" value={s.default_additional_credit != null ? `$${s.default_additional_credit.toLocaleString()}` : "—"}/>
        <KvRow label="Default lead time" value={s.default_lead_days != null ? `${s.default_lead_days} days` : "—"}/>
        <KvRow label="QI window" value={s.qi_window_days != null ? `${s.qi_window_days} days` : "—"}/>
        <KvRow label="Default pickup window" value={s.default_pickup_window}/>
      </div>

      {/* Public profile */}
      <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-public">
        <SectionTitle icon={Store} title="Public profile" hint={`shown on /s/${t.slug || "—"}`}/>
        <KvRow label="Tagline" value={p.tagline}/>
        <KvRow label="About" value={p.about ? `${p.about.slice(0, 200)}${p.about.length > 200 ? "…" : ""}` : "—"}/>
        <KvRow label="Location" value={p.location}/>
        <KvRow label="Founded" value={p.founded_year}/>
        <KvRow label="Website" value={p.website}/>
        <KvRow label="Public contact email" value={p.contact_email}/>
        <KvRow label="Categories" value={Array.isArray(p.categories) ? p.categories.join(", ") : p.categories}/>
        <KvRow label="MOQ" value={p.moq}/>
        <KvRow label="Warranty" value={p.warranty}/>
        <KvRow label="Ships from" value={p.ships_from}/>
        <KvRow label="Ships to" value={p.ships_to}/>
        <KvRow label="Restocking fee" value={p.restocking_fee_pct != null ? `${p.restocking_fee_pct}%` : "—"}/>
        <KvRow label="Drop-ship eligible" value={p.dropship_eligible ? "Yes" : "No"}/>
        <KvRow label="Seller-arranged shipping" value={p.offer_seller_arranged_shipping ? "Yes" : "No"}/>
        <KvRow label="Offers prebook" value={p.offers_prebook ? "Yes" : "No"}/>
        {(social.instagram || social.tiktok || social.linkedin || social.x || social.facebook || social.youtube) && (
          <div className="mt-2 pt-2 border-t border-border/40">
            <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 mb-1">Social handles</div>
            <KvRow label="Instagram" value={social.instagram} mono/>
            <KvRow label="TikTok" value={social.tiktok} mono/>
            <KvRow label="LinkedIn" value={social.linkedin} mono/>
            <KvRow label="X / Twitter" value={social.x} mono/>
            <KvRow label="Facebook" value={social.facebook} mono/>
            <KvRow label="YouTube" value={social.youtube} mono/>
          </div>
        )}
        {p.terms_and_conditions && (
          <div className="mt-3 pt-3 border-t border-border/40">
            <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/50 mb-1">Custom T&C</div>
            <pre className="text-[11.5px] text-foreground/75 whitespace-pre-wrap font-mono bg-secondary/40 rounded p-2 max-h-44 overflow-auto">
              {p.terms_and_conditions}
            </pre>
          </div>
        )}
      </div>

      {/* Domain */}
      <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-domain">
        <SectionTitle icon={Globe} title="Domain"/>
        <KvRow label="Slug" value={t.slug} mono/>
        <KvRow label="Auto subdomain" value={t.auto_subdomain} mono/>
        <KvRow label="Custom domain" value={t.custom_domain} mono/>
        <KvRow label="Custom domain status" value={t.custom_domain_status}/>
        <KvRow label="Provider" value={t.custom_domain_provider}/>
      </div>

      {/* Payout schedule */}
      <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-payout">
        <SectionTitle icon={Wallet} title="Auto-payout schedule"/>
        {data.payout_schedule ? (
          <>
            <KvRow label="Enabled" value={data.payout_schedule.enabled ? "Yes" : "No"}/>
            <KvRow label="Frequency" value={data.payout_schedule.frequency}/>
            <KvRow label="Day of week" value={lastFour(data.payout_schedule.day_of_week)}/>
            <KvRow label="Day of month" value={lastFour(data.payout_schedule.day_of_month)}/>
            <KvRow label="Minimum balance" value={data.payout_schedule.minimum_balance != null ? `$${data.payout_schedule.minimum_balance}` : "—"}/>
            <KvRow label="Destination method" value={data.payout_schedule.destination_method_id} mono/>
          </>
        ) : (
          <div className="text-[12px] text-foreground/55">No payout schedule configured — manual payouts only.</div>
        )}
      </div>

      {/* Setup status */}
      {data.setup_status && (
        <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-setup">
          <SectionTitle icon={CheckCircle2} title="Onboarding checklist"
            hint={data.setup_status.complete
              ? `Completed ${fmtDate(data.setup_status.completed_at)}`
              : `${data.setup_status.completed_steps}/${data.setup_status.total_steps} steps complete`}/>
          <ul className="space-y-1.5">
            {(data.setup_status.steps || []).map(s => (
              <li key={s.id} className="flex items-center gap-2 text-[12px]">
                {s.complete
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600"/>
                  : <XCircle className="w-3.5 h-3.5 text-foreground/30"/>}
                <span className={s.complete ? "text-foreground/80" : "text-foreground/55"}>{s.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TeamTab({ data, onChange }) {
  const members = data.team_members || [];
  const tenantId = data.tenant?.id;
  return (
    <div className="space-y-5">
      <div>
        <SectionTitle icon={Users} title="Team members"
          hint={`${members.length} on this account · max 10`}/>
        {members.length === 0 ? (
          <Empty icon={Users} label="Solo account — no team invited."/>
        ) : (
          <ul className="rounded-xl border border-border bg-white divide-y divide-border" data-testid="acc-360-team-list">
            {members.map(m => (
              <li key={m.id} className="px-4 py-3" data-testid={`acc-360-team-${m.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-secondary grid place-items-center shrink-0">
                    <UserIcon className="w-3.5 h-3.5 text-foreground/55"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium truncate">{m.name || m.email}</div>
                    <div className="text-[11.5px] text-foreground/55 mono truncate">{m.email}</div>
                  </div>
                  <Chip tone={m.role === "owner" ? "indigo" : "neutral"}>{m.role}</Chip>
                  <Chip tone={m.status === "active" ? "emerald" : "amber"}>{m.status}</Chip>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Tenant-scoped invites — sellers only */}
      {tenantId && (
        <div data-testid="acc-360-team-invites">
          <TenantInvitesPanel tenantId={tenantId} onChange={onChange}/>
        </div>
      )}
    </div>
  );
}

// ── Admin · Seller Domain support tab ─────────────────────────────────────
// Surfaces every URL the seller has configured, the live custom-domain
// DNS status, the most recent auto-configure attempt (incl. the raw CF
// error that blocked the seller — invaluable for support), and a focused
// audit timeline. Three admin actions are exposed:
//   1. Re-check DNS  → forces a Cloudflare-SaaS status poll
//   2. Make primary  → flips the primary_url_kind without the active gate
//   3. Disconnect    → wipes a stuck custom_domain that's blocking the seller
function DomainTab({ data, onChange }) {
  const da = data?.domain_activity;
  const [busy, setBusy] = useState(false);
  const uid = data?.user?.id;
  if (!da) return <Empty icon={Globe} label="No domain activity (this user has no tenant, or domain settings haven't been touched)."/>;
  const urls = da.urls || {};
  const ac   = da.auto_configure || null;
  const primary = da.primary || {};

  const recheck = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/admin/users/${uid}/domain/recheck`);
      toast.success(`Status: ${r?.status?.status || "unknown"}`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Re-check failed");
    } finally { setBusy(false); }
  };
  const makePrimary = async (kind) => {
    const reason = window.prompt(`Reason for making "${kind}" the primary URL?`, "Admin support");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.post(`/admin/users/${uid}/domain/set-primary`, { kind, reason: reason.trim() });
      toast.success(`Primary set to ${kind}`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't set primary");
    } finally { setBusy(false); }
  };
  const disconnect = async () => {
    const reason = window.prompt(`Force-disconnect ${urls.custom?.url}?\n\nReason for the audit log:`, "Admin support");
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${uid}/domain/custom`, { data: { reason: reason.trim() } });
      toast.success("Custom domain disconnected");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Disconnect failed");
    } finally { setBusy(false); }
  };

  const statusTone = (s) => {
    if (s === "active")  return "bg-emerald-500/15 text-emerald-700 border-emerald-500/30";
    if (s === "failed")  return "bg-rose-500/15 text-rose-700 border-rose-500/30";
    if (s) return "bg-amber-500/15 text-amber-700 border-amber-500/30";
    return "bg-foreground/10 text-foreground/55";
  };

  const Card = ({ kind, label, url, tag, set_at, extra }) => {
    const isPrimary = primary.kind === kind;
    return (
      <div className={`rounded-xl border ${isPrimary ? "border-emerald-500/40 bg-emerald-50/20" : "border-border"} p-3.5`} data-testid={`admin-domain-card-${kind}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10.5px] mono uppercase tracking-widest text-foreground/55">{label}</div>
            {url ? (
              <a href={`https://${url}`} target="_blank" rel="noreferrer" className="mt-1 block font-mono text-[13px] text-foreground/85 break-all hover:underline">{url}</a>
            ) : (
              <div className="mt-1 text-[12px] italic text-foreground/40">Not configured</div>
            )}
            {tag && <div className="mt-1 text-[11px] text-foreground/55">{tag}</div>}
            {set_at && <div className="mt-1 text-[10.5px] text-foreground/45">Set: {new Date(set_at).toLocaleString()}</div>}
            {extra}
          </div>
          <div className="shrink-0 flex flex-col items-end gap-1">
            {isPrimary && <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-semibold uppercase tracking-wider">Primary</span>}
            {url && !isPrimary && (
              <Button size="sm" variant="outline" className="rounded-full h-7 text-[11px]"
                      disabled={busy} onClick={() => makePrimary(kind)}
                      data-testid={`admin-domain-make-primary-${kind}`}>
                Make primary
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="domain-tab">
      {/* Top: 3 URL cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card
          kind="account"
          label="Account URL · permanent"
          url={urls.account?.url}
          tag={urls.account?.short_id ? `ID: ${urls.account.short_id}` : null}
          set_at={urls.account?.set_at}
        />
        <Card
          kind="branded"
          label="Branded subdomain"
          url={urls.branded?.url || urls.auto?.url}
          tag={urls.branded?.url ? "User-customized" : (urls.auto?.url ? "Default (auto from brand)" : null)}
          set_at={urls.branded?.set_at || urls.auto?.provisioned_at}
        />
        <Card
          kind="custom"
          label="Custom domain · white-label"
          url={urls.custom?.url}
          set_at={urls.custom?.added_at}
          extra={
            <div className="mt-2 space-y-1">
              {urls.custom?.status && (
                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border ${statusTone(urls.custom.status)}`}>
                  {urls.custom.status.replace(/_/g, " ")}
                </span>
              )}
              {urls.custom?.cf_ssl_status && (
                <div className="text-[10.5px] text-foreground/55">SSL: <span className="mono">{urls.custom.cf_ssl_status}</span></div>
              )}
              {urls.custom?.last_error && (
                <div className="text-[10.5px] text-rose-700 leading-snug">⚠ {urls.custom.last_error.slice(0, 220)}</div>
              )}
            </div>
          }
        />
      </div>

      {/* Admin actions row */}
      <div className="rounded-xl border border-border bg-secondary/30 p-3.5">
        <div className="text-[11px] mono uppercase tracking-widest text-foreground/55 mb-2.5">Support actions</div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" className="rounded-full" disabled={busy || !urls.custom?.url}
                  onClick={recheck} data-testid="admin-domain-recheck">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5"/>Re-check DNS
          </Button>
          <Button size="sm" variant="outline" className="rounded-full text-rose-700 hover:bg-rose-50"
                  disabled={busy || !urls.custom?.url} onClick={disconnect}
                  data-testid="admin-domain-disconnect">
            <Trash2 className="w-3.5 h-3.5 mr-1.5"/>Force-disconnect custom domain
          </Button>
        </div>
        <div className="mt-2 text-[10.5px] text-foreground/55 leading-snug">
          Every action here is audited in <span className="mono">admin_overrides</span> with the reason you provide.
        </div>
      </div>

      {/* Auto-configure run history */}
      {ac && (
        <div className="rounded-xl border border-border p-3.5">
          <div className="text-[11px] mono uppercase tracking-widest text-foreground/55 mb-2.5">Last auto-configure attempt</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px]">
            <Row k="Provider"     v={ac.dns_provider || ac.provider}/>
            <Row k="Requested at" v={ac.requested_at ? new Date(ac.requested_at).toLocaleString() : "—"}/>
            <Row k="Completed at" v={ac.completed_at ? new Date(ac.completed_at).toLocaleString() : "—"}/>
            <Row k="DNS action"   v={ac.dns_action || "—"}/>
            <Row k="DNS success"  v={
              <span className={ac.dns_success ? "text-emerald-700" : "text-rose-700"}>
                {ac.dns_success ? "✓ yes" : "✗ no"}
              </span>
            }/>
            <Row k="Zone ID"      v={<span className="mono">{ac.dns_zone_id || "—"}</span>}/>
          </div>
          {ac.dns_error && (
            <div className="mt-3 rounded-lg border border-rose-300 bg-rose-50/50 p-2.5 text-[11.5px] text-rose-900 leading-snug">
              <div className="mono uppercase text-[10px] tracking-widest text-rose-700 mb-1">DNS error</div>
              {ac.dns_error}
            </div>
          )}
        </div>
      )}

      {/* Domain audit timeline */}
      {(da.audit || []).length > 0 && (
        <div className="rounded-xl border border-border p-3.5">
          <div className="text-[11px] mono uppercase tracking-widest text-foreground/55 mb-2.5">Domain history ({da.audit.length})</div>
          <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
            {da.audit.map((a, i) => (
              <div key={a.id || i} className="text-[11.5px] flex items-start gap-2 border-b border-border/40 pb-1.5 last:border-0">
                <div className="shrink-0 w-32 mono text-foreground/55 text-[10.5px]">
                  {a.at ? new Date(a.at).toLocaleString() : "—"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-foreground/85 font-medium">{a.action || a.event}</div>
                  {a.details?.reason && <div className="text-foreground/60 leading-snug">{a.details.reason}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Tiny key/value row helper used by DomainTab.
function Row({ k, v }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className="text-foreground/55 text-[11px] uppercase tracking-wider min-w-[110px]">{k}</div>
      <div className="text-foreground/85">{v ?? "—"}</div>
    </div>
  );
}


function NotificationsTab({ data }) {
  const prefs = data.notification_prefs;
  if (!prefs) return <Empty icon={Bell} label="No notification preferences set (using defaults)."/>;
  const email = prefs.email || {};
  const sms = prefs.sms || {};
  const keys = Array.from(new Set([...Object.keys(email), ...Object.keys(sms)])).sort();  return (
    <div className="space-y-3">
      <SectionTitle icon={Bell} title="Notification preferences"
        hint="Snapshot of the toggles this user has set across Email and SMS channels."/>
      {keys.length === 0 ? (
        <Empty icon={Bell} label="No event preferences saved yet."/>
      ) : (
        <div className="rounded-xl border border-border bg-white overflow-hidden" data-testid="acc-360-notif-table">
          <table className="w-full text-[12.5px]">
            <thead className="bg-secondary/50 text-[10px] mono uppercase tracking-[0.18em] text-foreground/55">
              <tr>
                <th className="text-left px-4 py-2.5 font-normal">Event</th>
                <th className="text-center px-4 py-2.5 font-normal w-20">Email</th>
                <th className="text-center px-4 py-2.5 font-normal w-20">SMS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {keys.map(k => (
                <tr key={k}>
                  <td className="px-4 py-2.5">{k.replace(/_/g, " ")}</td>
                  <td className="px-4 py-2.5 text-center">{email[k] ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline"/> : <span className="text-foreground/30">—</span>}</td>
                  <td className="px-4 py-2.5 text-center">{sms[k] ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 inline"/> : <span className="text-foreground/30">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SecurityTab({ data }) {
  const tf = data.twofa || {};
  return (
    <div className="space-y-5">
      <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-2fa">
        <SectionTitle icon={KeyRound} title="Two-factor authentication"/>
        <KvRow label="Authenticator (TOTP)" value={tf.totp_enabled ? "Enabled" : "Off"}/>
        <KvRow label="SMS 2FA"             value={tf.sms_enabled ? "Enabled" : "Off"}/>
        <KvRow label="SMS phone"           value={tf.sms_phone || "—"} mono/>
      </div>

      <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-sessions">
        <SectionTitle icon={Smartphone} title="Active sessions"
          hint={`${data.sessions_active_count || 0} active · showing latest 20`}/>
        {(data.sessions || []).length === 0 ? (
          <Empty icon={Smartphone} label="No active sessions tracked."/>
        ) : (
          <ul className="divide-y divide-border">
            {data.sessions.map(s => (
              <li key={s.id} className="py-2.5 text-[12px]" data-testid={`acc-360-session-${s.id}`}>
                <div className="font-medium">{s.device || "Unknown device"}</div>
                <div className="text-[11px] text-foreground/55 mono">
                  {s.ip && s.ip !== "—" ? `${s.ip} · ` : ""}
                  Signed in {fmtDate(s.created_at)}
                  {s.last_active_at && s.last_active_at !== s.created_at && ` · last active ${fmtDate(s.last_active_at)}`}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-audit">
        <SectionTitle icon={ScrollText} title="Recent activity"
          hint={`${(data.audit_log || []).length} entries (most recent 25)`}/>
        {(data.audit_log || []).length === 0 ? (
          <Empty icon={ScrollText} label="No audit events for this account yet."/>
        ) : (
          <ul className="divide-y divide-border">
            {(data.audit_log || []).slice(0, 25).map((a, i) => (
              <li key={a.id || i} className="py-2 text-[12px]">
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3 text-foreground/40"/>
                  <span className="flex-1">{a.message || a.kind || a.action || "—"}</span>
                  <span className="text-[10.5px] text-foreground/50 mono">{(a.created_at || a.at) ? fmtDate(a.created_at || a.at).split(",")[0] : ""}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Wallet tab — balance + ledger + cards + payout-run audit ──────────
function WalletTab({ data }) {
  const w = data.wallet || {};
  const txns = data.wallet_txns || [];
  const cards = data.cards || [];
  const runs = data.payout_runs || [];
  const [typeFilter, setTypeFilter] = useState("all");
  const TYPE_OPTIONS = useMemo(() => {
    const set = new Set(txns.map(t => (t.type || "").toLowerCase()).filter(Boolean));
    return ["all", ...Array.from(set).sort()];
  }, [txns]);
  const filtered = useMemo(() => {
    if (typeFilter === "all") return txns;
    return txns.filter(t => (t.type || "").toLowerCase() === typeFilter);
  }, [txns, typeFilter]);

  return (
    <div className="space-y-5">
      {/* Balance + counters */}
      <div className="rounded-xl bg-gradient-to-br from-emerald-50/60 to-secondary border border-border p-4" data-testid="acc-360-wallet-balance">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Wallet balance</div>
            <div className="display text-3xl tracking-tight mt-1">{money(w.balance)} <span className="text-[12px] text-foreground/55 mono ml-1">{w.currency || "USD"}</span></div>
          </div>
          <div className="text-right">
            <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">Lifetime txns</div>
            <div className="display text-xl mt-0.5">{w.tx_count ?? 0}</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-border/40">
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/50">Inflows · 30d</div>
            <div className="text-emerald-700 mono text-[14px] mt-0.5">+{money(w.in_30d)}</div>
          </div>
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/50">Outflows · 30d</div>
            <div className="text-rose-700 mono text-[14px] mt-0.5">{money(w.out_30d)}</div>
          </div>
          <div>
            <div className="text-[10px] mono uppercase tracking-[0.16em] text-foreground/50">Net · 30d</div>
            <div className={`mono text-[14px] mt-0.5 ${(w.net_30d || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{money(w.net_30d)}</div>
          </div>
        </div>
      </div>

      {/* Cards (when seller has any) */}
      {cards.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-cards">
          <SectionTitle icon={CreditCard} title="Issued virtual cards"
            hint={`${cards.length} card${cards.length === 1 ? "" : "s"} · PAN/CVC stripped server-side`}/>
          <ul className="divide-y divide-border/40">
            {cards.map(c => (
              <li key={c.id} className="py-2 flex items-center gap-3 text-[12.5px]" data-testid={`acc-360-card-${c.id}`}>
                <CreditCard className="w-3.5 h-3.5 text-foreground/55"/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{c.label || `Card ····${c.last4 || ""}`}</div>
                  <div className="text-[11px] text-foreground/55 mono">
                    {c.brand || "card"} ····{c.last4 || "—"}
                    {c.spend_limit != null && ` · limit ${money(c.spend_limit)}`}
                  </div>
                </div>
                <Chip tone={c.status === "frozen" ? "rose" : c.status === "active" ? "emerald" : "neutral"}>
                  {c.status || "—"}
                </Chip>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Payout schedule (re-surface here too — single source of truth) */}
      {data.payout_schedule && (
        <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-wallet-payout-sched">
          <SectionTitle icon={CalendarClock} title="Auto-payout schedule"/>
          <KvRow label="Enabled" value={data.payout_schedule.enabled ? "Yes" : "No"}/>
          <KvRow label="Frequency" value={data.payout_schedule.frequency}/>
          <KvRow label="Minimum balance" value={data.payout_schedule.minimum_balance != null ? money(data.payout_schedule.minimum_balance) : "—"}/>
          <KvRow label="Last run" value={data.payout_schedule.last_run_at ? fmtDate(data.payout_schedule.last_run_at) : "—"}/>
        </div>
      )}

      {/* Recent payout runs */}
      {runs.length > 0 && (
        <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-payout-runs">
          <SectionTitle icon={ScrollText} title="Recent payout-scheduler runs"
            hint={`${runs.length} runs (most recent first · paid/skipped/failed)`}/>
          <ul className="divide-y divide-border/40">
            {runs.slice(0, 10).map(r => (
              <li key={r.id} className="py-2 text-[12px]">
                <div className="flex items-center gap-2">
                  <Chip tone={r.decision === "paid" ? "emerald" : r.decision === "failed" ? "rose" : "amber"}>
                    {r.decision}
                  </Chip>
                  <span className="flex-1 text-foreground/70">{r.reason || "—"}</span>
                  {r.amount && <span className="mono text-foreground/85">{money(r.amount)}</span>}
                </div>
                <div className="text-[10.5px] text-foreground/45 mono mt-0.5">{fmtDate(r.run_at)}</div>
                {r.error && <div className="text-[11px] text-rose-600 mt-0.5">{r.error}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Ledger */}
      <div className="rounded-xl border border-border bg-white p-4" data-testid="acc-360-ledger">
        <div className="flex items-center justify-between mb-2">
          <SectionTitle icon={Wallet} title="Wallet ledger"
            hint={`${filtered.length} of ${txns.length} txns shown · last 100 cached`}/>
          {TYPE_OPTIONS.length > 1 && (
            <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
              data-testid="acc-360-ledger-filter"
              className="h-7 rounded-md border border-foreground/15 bg-white px-2 text-[11.5px]">
              {TYPE_OPTIONS.map(t => (
                <option key={t} value={t}>{t === "all" ? "All types" : t}</option>
              ))}
            </select>
          )}
        </div>
        {filtered.length === 0 ? (
          <Empty icon={Wallet} label="No transactions match this filter."/>
        ) : (
          <ul className="divide-y divide-border/40">
            {filtered.map(t => {
              const amt = Number(t.amount || 0);
              const isIn = amt > 0;
              return (
                <li key={t.id} className="py-2 text-[12px]" data-testid={`acc-360-txn-${t.id}`}>
                  <div className="flex items-center gap-2">
                    {isIn
                      ? <ArrowDownRight className="w-3.5 h-3.5 text-emerald-600 shrink-0"/>
                      : <ArrowUpRight className="w-3.5 h-3.5 text-rose-600 shrink-0"/>}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium flex items-center gap-1.5">
                        {t.type}
                        {t.method && <span className="text-foreground/45 text-[11px] mono">· {t.method}</span>}
                        {t.status && t.status !== "completed" && (
                          <Chip tone={t.status === "failed" ? "rose" : "amber"}>{t.status}</Chip>
                        )}
                      </div>
                      <div className="text-[10.5px] text-foreground/55 mono">
                        {t.ref || "—"}{t.memo ? ` · ${t.memo}` : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`mono ${isIn ? "text-emerald-700" : "text-rose-700"} font-medium`}>
                        {isIn ? "+" : ""}{money(amt)}
                      </div>
                      <div className="text-[10.5px] text-foreground/45 mono">
                        {(t.created_at || "").slice(0, 10)}
                      </div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Banking · Plaid · Credit · Buyer Hub tab ────────────────────────────
function BankingTab({ data }) {
  const plaid  = data.plaid_items || [];
  const credit = data.credit_apps || [];
  const xpos   = data.external_pos || [];
  const xinvs  = data.external_invoices || [];
  const role   = data.user.role;
  return (
    <div className="space-y-5">
      <div>
        <SectionTitle title="Plaid bank connections" sub={`${plaid.length} item(s)`}/>
        {plaid.length === 0 ? <Empty icon={Landmark} label="No Plaid items linked."/> : (
          <ul className="divide-y divide-border/40 border border-border/40 rounded-lg overflow-hidden" data-testid="acc-360-plaid">
            {plaid.map((p, i) => (
              <li key={i} className="p-3 text-[12px]" data-testid={`plaid-item-${i}`}>
                <div className="flex justify-between items-baseline">
                  <div className="font-medium">{p.institution_name || "Linked bank"}</div>
                  <span className="text-[10.5px] mono text-foreground/55">{p.connected_at ? new Date(p.connected_at).toLocaleDateString() : "—"}</span>
                </div>
                <div className="mt-1 text-[11px] text-foreground/65">
                  {(p.accounts || []).length} account(s)
                  {p.cashflow?.avg_monthly_revenue && <> · avg ${Number(p.cashflow.avg_monthly_revenue).toLocaleString()}/mo</>}
                  {typeof p.cashflow?.nsf_count === "number" && <> · {p.cashflow.nsf_count} NSF</>}
                  {p.cashflow?.months_of_data && <> · {p.cashflow.months_of_data}mo history</>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <SectionTitle title="Credit applications" sub={`${credit.length} record(s)`}/>
        {credit.length === 0 ? <Empty icon={CreditCard} label="No credit applications on file."/> : (
          <ul className="divide-y divide-border/40 border border-border/40 rounded-lg overflow-hidden" data-testid="acc-360-credit-apps">
            {credit.slice(0, 10).map((c, i) => (
              <li key={c.id || i} className="p-3 text-[12px]" data-testid={`credit-app-${i}`}>
                <div className="flex justify-between items-baseline">
                  <div className="font-medium">
                    {c.terms || c.term || "Net-30"} · ${Number(c.requested_amount || c.limit || 0).toLocaleString()}
                  </div>
                  <Chip tone={
                    (c.status || "").includes("approved") ? "emerald" :
                    (c.status || "").includes("denied")   ? "rose"   :
                    (c.status || "").includes("pending")  ? "amber"  : "neutral"
                  }>{c.status || "—"}</Chip>
                </div>
                <div className="mt-1 text-[10.5px] mono text-foreground/55">
                  {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
                  {c.seller_name && <> · seller {c.seller_name}</>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {role === "buyer" && (
        <>
          <div>
            <SubSectionTitle title="Buyer Hub · external POs" sub={`${xpos.length} off-platform PO(s)`}/>
            {xpos.length === 0 ? <Empty icon={FileText} label="No external POs."/> : (
              <ul className="divide-y divide-border/40 border border-border/40 rounded-lg overflow-hidden" data-testid="acc-360-ext-pos">
                {xpos.slice(0, 10).map((p, i) => (
                  <li key={p.id || i} className="p-3 text-[12px]" data-testid={`ext-po-${i}`}>
                    <div className="flex justify-between">
                      <span className="font-medium">{p.supplier_name || p.vendor_name || "Unknown supplier"}</span>
                      <span className="mono">{money(p.amount || p.total)}</span>
                    </div>
                    <div className="mt-0.5 text-[10.5px] mono text-foreground/55">
                      {p.po_number || (p.id || "").slice(0, 8)}
                      {p.created_at && <> · {new Date(p.created_at).toLocaleDateString()}</>}
                      {p.status && <> · {p.status}</>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <SubSectionTitle title="Buyer Hub · external invoices (verqo Capital financing)" sub={`${xinvs.length} invoice(s)`}/>
            {xinvs.length === 0 ? <Empty icon={Receipt} label="No external invoices."/> : (
              <ul className="divide-y divide-border/40 border border-border/40 rounded-lg overflow-hidden" data-testid="acc-360-ext-invs">
                {xinvs.slice(0, 10).map((iv, i) => (
                  <li key={iv.id || i} className="p-3 text-[12px]" data-testid={`ext-inv-${i}`}>
                    <div className="flex justify-between">
                      <span className="font-medium">{iv.supplier_name || iv.vendor_name || "Unknown"}</span>
                      <span className="mono">{money(iv.amount)}</span>
                    </div>
                    <div className="mt-0.5 text-[10.5px] mono text-foreground/55 flex items-center gap-1.5">
                      {iv.code || iv.number || (iv.id || "").slice(0, 8)}
                      <Chip tone={iv.status === "financed" ? "emerald" : "neutral"}>{iv.status || "uploaded"}</Chip>
                      {iv.financing_fee > 0 && <span>· fee ${iv.financing_fee.toFixed(2)}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── verqo revenue contributed by this user ─────────────────────────────
function RevenueTab({ data }) {
  const r = data.revenue_from_user || { lifetime_fee: 0, lifetime_gross: 0, lifetime_txn_count: 0, by_source: [] };
  const fmt = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border bg-gradient-to-br from-foreground to-foreground/80 text-background p-4" data-testid="revenue-from-user">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] opacity-65">verqo Revenue contributed by this account</div>
        <div className="mt-1 text-3xl font-bold tabular-nums">{fmt(r.lifetime_fee)}</div>
        <div className="text-[11.5px] opacity-75 mt-1">
          {r.lifetime_txn_count} txn(s) · {fmt(r.lifetime_gross)} gross processed
        </div>
      </div>

      <div>
        <SubSectionTitle title="Revenue by source" sub="Per-stream breakdown of every verqo commission moment"/>
        {r.by_source.length === 0 ? <Empty icon={DollarSign} label="No revenue captured from this account yet."/> : (
          <ul className="divide-y divide-border/40 border border-border/40 rounded-lg overflow-hidden" data-testid="acc-360-rev-by-source">
            {r.by_source.map(s => (
              <li key={s.source} className="p-3 text-[12px] flex items-center justify-between" data-testid={`rev-src-${s.source}`}>
                <div>
                  <div className="font-medium capitalize">{s.source.replace(/_/g, " ")}</div>
                  <div className="text-[10.5px] mono text-foreground/55 mt-0.5">
                    {s.count} txn(s) · {fmt(s.gross)} gross
                  </div>
                </div>
                <div className="text-right tabular-nums">
                  <div className="text-emerald-700 font-semibold">{fmt(s.fee)}</div>
                  <div className="text-[10.5px] text-foreground/55">
                    {s.gross > 0 ? `${((s.fee / s.gross) * 100).toFixed(2)}%` : "—"} effective
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SubSectionTitle({ title, sub }) {
  return (
    <div className="mb-2">
      <div className="text-[11px] mono uppercase tracking-[0.18em] text-foreground/70">{title}</div>
      {sub && <div className="text-[10.5px] text-foreground/45 mt-0.5">{sub}</div>}
    </div>
  );
}



// ── Activity tab (CC-2 · unified user + admin timeline) ─────────────────
function ActivityTab({ userId }) {
  const [data, setData] = useState(null);
  const [kindFilter, setKindFilter] = useState(""); // "" | "user" | "admin"
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    setBusy(true);
    // Always fetch the full set so the 3 KPI tiles reflect absolute totals.
    // Filtering is then applied client-side over `data.items`.
    const params = new URLSearchParams({ since_days: "90", limit: "200" });
    api.get(`/admin/users/${userId}/activity?${params}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load activity"))
      .finally(() => { if (alive) setBusy(false); });
    return () => { alive = false; };
  }, [userId]);

  if (busy && !data) {
    return (
      <div className="flex items-center justify-center py-10 text-foreground/55">
        <Loader2 className="w-4 h-4 animate-spin mr-2"/>Loading activity…
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary || {};
  const visibleItems = kindFilter
    ? (data.items || []).filter(i => i.kind === kindFilter)
    : (data.items || []);
  return (
    <div className="space-y-4" data-testid="activity-tab">
      <div className="grid grid-cols-3 gap-2">
        <button onClick={() => setKindFilter("")}
          className={`text-left rounded-lg border p-2.5 ${kindFilter === "" ? "bg-foreground/5 border-foreground/40" : "border-border"}`}
          data-testid="activity-filter-all">
          <div className="text-[10px] uppercase tracking-wider text-foreground/55">All events</div>
          <div className="text-xl font-semibold">{s.total ?? 0}</div>
        </button>
        <button onClick={() => setKindFilter("user")}
          className={`text-left rounded-lg border p-2.5 ${kindFilter === "user" ? "bg-sky-50 border-sky-300" : "border-border"}`}
          data-testid="activity-filter-user">
          <div className="text-[10px] uppercase tracking-wider text-sky-700">User actions</div>
          <div className="text-xl font-semibold">{s.user ?? 0}</div>
        </button>
        <button onClick={() => setKindFilter("admin")}
          className={`text-left rounded-lg border p-2.5 ${kindFilter === "admin" ? "bg-violet-50 border-violet-300" : "border-border"}`}
          data-testid="activity-filter-admin">
          <div className="text-[10px] uppercase tracking-wider text-violet-700">Admin overrides</div>
          <div className="text-xl font-semibold">{s.admin ?? 0}</div>
        </button>
      </div>

      <div className="text-[11px] text-foreground/55">
        Last 90 days · hash-chained on both sides for tamper evidence.
      </div>

      <div className="rounded-xl border divide-y" data-testid="activity-list">
        {visibleItems.length === 0 && (
          <div className="px-3 py-6 text-center text-[12px] text-foreground/45">
            No activity in the selected window.
          </div>
        )}
        {visibleItems.map(item => (
          <ActivityRow key={`${item.kind}-${item.id}`} item={item}/>
        ))}
      </div>
    </div>
  );
}


function ActivityRow({ item }) {
  const isAdmin = item.kind === "admin";
  const tone = isAdmin ? "violet" : "sky";
  const Icon = isAdmin ? ShieldX : UserIcon;
  return (
    <div className="px-3 py-2.5 flex gap-3" data-testid={`activity-row-${item.id}`}>
      <div className={`shrink-0 w-7 h-7 rounded-full grid place-items-center bg-${tone}-50 text-${tone}-700`}>
        <Icon className="w-3.5 h-3.5"/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[12.5px] font-medium truncate">
            <span className={`chip text-[10px] mr-1.5 bg-${tone}-50 border-${tone}-200 text-${tone}-800`}>
              {isAdmin ? "ADMIN" : "USER"}
            </span>
            {item.action}
            {item.entity_type && (
              <span className="text-foreground/55"> · {item.entity_type}</span>
            )}
          </div>
          <div className="text-[10.5px] mono text-foreground/55 shrink-0">
            {item.created_at ? new Date(item.created_at).toLocaleString() : ""}
          </div>
        </div>
        <div className="text-[11px] text-foreground/55 mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
          {item.entity_id && <span className="mono">id: {String(item.entity_id).slice(0, 16)}…</span>}
          {item.actor_email && <span>actor: {item.actor_email}</span>}
          {item.reason && <span>reason: "{item.reason}"</span>}
          {typeof item.impact_usd === "number" && item.impact_usd > 0 && (
            <span className="text-amber-700">impact: ${item.impact_usd.toLocaleString()}</span>
          )}
          {item.ip && <span className="mono">ip: {item.ip}</span>}
          {item.chain_position && <span className="mono text-foreground/40">#{item.chain_position}</span>}
        </div>
        {(item.before && Object.keys(item.before).length > 0) || (item.after && Object.keys(item.after).length > 0) ? (
          <details className="mt-1">
            <summary className="text-[10.5px] text-foreground/55 cursor-pointer hover:text-foreground/80">
              diff
            </summary>
            <pre className="text-[10.5px] mono bg-foreground/3 rounded p-2 mt-1 overflow-auto max-h-32">
{JSON.stringify({ before: item.before || {}, after: item.after || {} }, null, 2)}
            </pre>
          </details>
        ) : null}
      </div>
    </div>
  );
}


// ── Login Diagnostics tab ───────────────────────────────────────────────
// "Why can't this user log in right now?" — surfaces every gate that could
// block login + one-click admin fixes. Data: GET /admin/users/{id}/login-
// diagnostics + POST /admin/users/{id}/login-fix. Every fix is permanently
// logged to the admin-override hash chain.
function LoginDiagnosticsTab({ data }) {
  const u = data.user;
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users/${u.id}/login-diagnostics`);
      setDiag(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [u.id]);

  const runFix = async (action, label) => {
    const reason = window.prompt(
      `Apply "${label}" to ${u.email}?\n\nThis is permanently logged to the admin audit chain.\n\nReason (min 4 chars):`,
      ""
    );
    if (!reason || reason.trim().length < 4) return;
    setBusyAction(action);
    try {
      const r = await api.post(`/admin/users/${u.id}/login-fix`, {
        action, reason: reason.trim(),
      });
      toast.success(`✓ ${label} applied`, { description: JSON.stringify(r.data.applied) });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${label} failed`);
    } finally {
      setBusyAction(null);
    }
  };

  // Maps each blocker.fix code → button label
  const FIX_LABELS = {
    clear_lockout:           "Clear lockout",
    mark_email_verified:     "Mark email verified",
    mark_phone_verified:     "Mark phone verified",
    unsuspend:               "Unsuspend account",
    unfreeze:                "Unfreeze wallet",
    trigger_password_reset:  "Send password-reset email",
    clear_2fa_setup_required: "Clear 2FA-setup flag",
  };

  if (loading || !diag) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-foreground/40"/>
      </div>
    );
  }

  const canLogin = diag.status?.can_login_now;
  const blockers = diag.blockers || [];

  return (
    <div className="space-y-5" data-testid="login-diagnostics">
      {/* Headline: can_login_now */}
      <div className={`rounded-2xl p-5 border-2 ${
        canLogin
          ? "bg-emerald-50/60 border-emerald-200"
          : "bg-rose-50/60 border-rose-200"
      }`}>
        <div className="flex items-start gap-3">
          {canLogin
            ? <LogIn className="w-5 h-5 text-emerald-600 mt-0.5"/>
            : <Lock className="w-5 h-5 text-rose-600 mt-0.5"/>
          }
          <div className="flex-1">
            <h3 className={`text-[15px] font-semibold ${canLogin ? "text-emerald-900" : "text-rose-900"}`}>
              {canLogin
                ? "This user CAN log in right now."
                : "This user is BLOCKED from logging in."}
            </h3>
            {!canLogin && (
              <p className="mt-1.5 text-[12.5px] text-rose-800/85">
                {blockers.length} blocker{blockers.length === 1 ? "" : "s"} —
                use the "fix" buttons below or work through each one manually.
              </p>
            )}
            {canLogin && blockers.length > 0 && (
              <p className="mt-1.5 text-[12.5px] text-emerald-800/85">
                Has {blockers.length} non-blocking warning{blockers.length === 1 ? "" : "s"} (yellow markers below). Login still works.
              </p>
            )}
          </div>
          <button onClick={load} className="text-foreground/50 hover:text-foreground" title="Refresh">
            <RefreshCw className="w-4 h-4"/>
          </button>
        </div>
      </div>

      {/* Blocker list with fix actions */}
      {blockers.length > 0 && (
        <div className="rounded-xl border border-border bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-slate-50">
            <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/60">Active blockers</div>
          </div>
          <div className="divide-y divide-border">
            {blockers.map((b, i) => {
              const sev = b.severity || "info";
              const color =
                sev === "block" ? "rose"  :
                sev === "warn"  ? "amber" : "slate";
              return (
                <div key={i} className="px-5 py-4 flex items-start gap-4" data-testid={`blocker-${b.code}`}>
                  <div className={`mt-1 w-2 h-2 rounded-full bg-${color}-500 shrink-0`}/>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] font-medium text-foreground">{b.label}</div>
                    <div className="mt-0.5 text-[10.5px] mono uppercase tracking-wider text-foreground/45">{b.code} · {sev}</div>
                  </div>
                  {b.fix && FIX_LABELS[b.fix] && (
                    <button
                      onClick={() => runFix(b.fix, FIX_LABELS[b.fix])}
                      disabled={busyAction === b.fix}
                      className={`shrink-0 px-3 py-1.5 rounded-lg text-[12px] font-semibold transition-all ${
                        sev === "block"
                          ? "bg-rose-600 text-white hover:bg-rose-700"
                          : "bg-slate-900 text-white hover:bg-slate-700"
                      } disabled:opacity-50`}
                      data-testid={`fix-${b.fix}`}
                    >
                      {busyAction === b.fix ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : FIX_LABELS[b.fix]}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Status booleans grid */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/60 mb-3">Status snapshot</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            ["Email verified",     diag.status.email_verified],
            ["Phone verified",     diag.status.phone_verified],
            ["Has password hash",  diag.status.has_password_hash],
            ["TOTP enrolled",      diag.status.totp_enrolled],
            ["Suspended",          !diag.status.suspended,  /* invert for ✓=good */],
            ["Frozen",             !diag.status.frozen],
            ["In lockout",         !diag.status.locked],
          ].map(([label, good], i) => (
            <div key={i} className="flex items-center gap-2 text-[12.5px]">
              {good
                ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0"/>
                : <XCircle      className="w-4 h-4 text-rose-500 shrink-0"/>}
              <span className="text-foreground/80">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Platform flag context */}
      <div className="rounded-xl border border-border bg-white p-5">
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/60 mb-3">
          Platform login policy (affects this user)
        </div>
        <div className="space-y-1.5 text-[12.5px]">
          <div>· Seller SMS-MFA required: <span className="mono">{diag.platform_flags.require_seller_sms_mfa ? "ON" : "off"}</span></div>
          <div>· Admin portal lockdown: <span className="mono">{diag.platform_flags.admin_portal_lockdown ? "ON" : "off"}</span></div>
          <div>· Admin SMS step-up required: <span className="mono">{diag.platform_flags.require_admin_sms_step_up ? "ON" : "off"}</span></div>
          <div className="pt-1.5 mt-2 border-t border-border text-foreground/55">
            Failed attempts in last 24h: <span className="mono text-foreground">{diag.metrics.failed_attempts_last_24h}</span>
          </div>
        </div>
      </div>

      {/* Recent attempts timeline */}
      <div className="rounded-xl border border-border bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-border bg-slate-50">
          <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/60">Last 10 login attempts</div>
        </div>
        {diag.recent_attempts.length === 0 ? (
          <div className="px-5 py-6 text-center text-[12.5px] text-foreground/55">
            No login attempts on record.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {diag.recent_attempts.map((r, i) => (
              <div key={i} className="px-5 py-3 flex items-start gap-3">
                {r.success
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-1 shrink-0"/>
                  : <XCircle      className="w-3.5 h-3.5 text-rose-500 mt-1 shrink-0"/>}
                <div className="flex-1 min-w-0">
                  <div className="text-[12.5px] flex flex-wrap items-baseline gap-x-2">
                    <span className="mono text-foreground/70">{(r.at || "").slice(0,19).replace("T"," ")}</span>
                    <span className="text-[10px] mono uppercase tracking-wider text-foreground/45">
                      {r.method || "?"} · {r.ip || "no-ip"}
                    </span>
                  </div>
                  {!r.success && r.reason && (
                    <div className="mt-0.5 text-[11.5px] text-rose-700">{r.reason}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


// ── Danger Zone tab ─────────────────────────────────────────────────────
// Permanently delete a user account + every row they own. Tucked away as
// the LAST tab inside the drawer so it can't be triggered accidentally
// from a table-row click. Two explicit confirmations are required:
//   1. Type the target's email exactly (typo protection)
//   2. Provide a reason (≥ 8 chars, written to the admin-override hash chain)
// The backend enforces all the same constraints + refuses self-delete and
// last-admin delete — see routes/admin_self_service.py.
function DangerZoneTab({ data, onDeleted }) {
  const u = data.user;
  const [confirmEmail, setConfirmEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const emailMatches = confirmEmail.trim().toLowerCase() === (u.email || "").toLowerCase();
  const reasonOk = reason.trim().length >= 8;
  const canSubmit = emailMatches && reasonOk && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    // One more browser confirm so a single accidental click can't fire it.
    const ok = window.confirm(
      `Final confirmation:\n\n` +
      `You're about to PERMANENTLY DELETE ${u.email} and EVERY row they own ` +
      `across orders, invoices, wallet, credit, insurance, etc.\n\n` +
      `This action is logged to the admin audit chain and cannot be undone.\n\n` +
      `Continue?`
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.delete(`/admin/users/${u.id}`, {
        data: { reason: reason.trim(), confirm_email: u.email },
      });
      const total = r.data?.total_rows_removed ?? 0;
      toast.success(
        `Deleted ${u.email} · ${total} row${total === 1 ? "" : "s"} removed across the platform.`,
        { duration: 6000 }
      );
      onDeleted?.(u.id);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
                 : detail?.message || e.message || "Delete failed";
      toast.error(msg, { duration: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="account-360-danger-zone">
      <div className="rounded-2xl border-2 border-rose-200 bg-rose-50/40 p-5">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 shrink-0"/>
          <div className="flex-1">
            <h3 className="text-[15px] font-semibold text-rose-900">Permanently delete this account</h3>
            <p className="mt-1.5 text-[12.5px] text-rose-800/85 leading-relaxed">
              Removes <span className="mono">{u.email}</span> and ALL of their owned data —
              orders, invoices, wallet, payment methods, credit applications, score history,
              insurance policies, claims, connections, invites, messages, notifications
              {u.role === "seller" ? ", their tenant (storefront + products)" : ""}, KYB
              artifacts, and audit log entries.
            </p>
            <p className="mt-2 text-[12px] mono uppercase tracking-wider text-rose-700">
              This action cannot be undone.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-5 space-y-4">
        <div>
          <label className="block text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/60 mb-1.5">
            Type the user's email to confirm
          </label>
          <Input
            value={confirmEmail}
            onChange={e => setConfirmEmail(e.target.value)}
            placeholder={u.email}
            className={`mono text-[13px] ${
              confirmEmail && !emailMatches ? "border-rose-400 focus-visible:ring-rose-300" : ""
            }`}
            data-testid="danger-zone-confirm-email"
          />
          {confirmEmail && !emailMatches && (
            <p className="mt-1.5 text-[11px] text-rose-700">Email doesn't match.</p>
          )}
        </div>

        <div>
          <label className="block text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/60 mb-1.5">
            Reason for deletion <span className="text-foreground/40 normal-case tracking-normal">(min 8 chars · written to audit log)</span>
          </label>
          <Input
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="GDPR deletion request · ticket #1234"
            className="text-[13px]"
            data-testid="danger-zone-reason"
          />
        </div>

        <div className="pt-2">
          <Button
            variant="destructive"
            disabled={!canSubmit}
            onClick={submit}
            className="w-full sm:w-auto"
            data-testid="danger-zone-delete-button"
          >
            {busy ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Deleting…</>
            ) : (
              <><Trash2 className="w-4 h-4 mr-2"/>Permanently delete {u.email}</>
            )}
          </Button>
        </div>

        <ul className="mt-4 text-[11.5px] text-foreground/55 space-y-1 pt-3 border-t border-border">
          <li>· Admins can't delete themselves (the current account).</li>
          <li>· The last remaining admin can't be deleted.</li>
          <li>· Each deletion is permanently logged in the admin-override hash chain.</li>
          <li>· Use <span className="mono">Suspend</span> in the Admin Controls tab if you want a reversible block instead.</li>
        </ul>
      </div>
    </div>
  );
}


// ── Drawer shell ────────────────────────────────────────────────────────
const TABS = [
  { id: "profile",  label: "Profile",  icon: UserIcon,    roles: ["buyer", "seller", "admin"] },
  { id: "business", label: "Business · KYB", icon: FileText, roles: ["buyer", "seller"] },
  { id: "addresses", label: "Addresses", icon: MapPin,    roles: ["buyer", "seller"] },
  { id: "payments", label: "Payment methods", icon: CreditCard, roles: ["buyer", "seller"] },
  { id: "wallet",   label: "Wallet",   icon: Wallet,     roles: ["buyer", "seller"] },
  { id: "banking",  label: "Banking · Credit · Hub", icon: Landmark, roles: ["buyer", "seller"] },
  { id: "revenue",  label: "verqo $",  icon: DollarSign, roles: ["buyer", "seller"] },
  { id: "controls", label: "Admin Controls", icon: Wrench, roles: ["buyer", "seller"] },
  { id: "activity", label: "Activity",  icon: Activity,   roles: ["buyer", "seller"] },
  { id: "settings", label: "Settings", icon: Settings2,  roles: ["seller"] },
  // Domain — surfaces all 3 storefront URLs, custom-domain DNS status,
  // auto-configure attempts, and audit trail. Lets support debug stuck
  // domains and force-disconnect or rotate primary without bouncing into
  // the seller's actual portal.
  { id: "domain",   label: "Domain",   icon: Globe,      roles: ["seller"] },
  { id: "team",     label: "Team",     icon: Users,      roles: ["buyer", "seller"] },
  { id: "notifs",   label: "Notifications", icon: Bell,  roles: ["buyer", "seller"] },
  { id: "security", label: "Security", icon: ShieldCheck, roles: ["buyer", "seller", "admin"] },
  // Login Diagnostics — answers "why can't this user log in?" with one-click
  // admin fixes. Lives right before Danger Zone for the natural support flow:
  // "first try to fix the login; if it can't be fixed, delete the account".
  { id: "login-diag", label: "Login Diagnostics", icon: Stethoscope, roles: ["buyer", "seller", "admin"] },
  // Danger Zone is the LAST tab on purpose — visually and structurally
  // separated from the rest so it can't be triggered accidentally.
  { id: "danger",   label: "Danger Zone", icon: AlertTriangle, roles: ["buyer", "seller", "admin"] },
];

export default function AdminAccount360Drawer({ user, onClose, onKycUpdated, onDeleted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("profile");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setData(null); setLoading(true); setTab("profile"); setNote("");
    api.get(`/admin/users/${user.id}/account-360`)
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || "Failed to load account"))
      .finally(() => setLoading(false));
  }, [user?.id]);

  const visibleTabs = useMemo(() => {
    if (!data) return TABS;
    const role = data.user.role;
    return TABS.filter(t => t.roles.includes(role));
  }, [data]);

  const reviewKyc = async (status) => {
    setBusy(true);
    try {
      const { data: fresh } = await api.patch(`/admin/users/${user.id}/kyc-verify`, { status, note: note || undefined });
      toast.success(`Marked as ${status}`);
      onKycUpdated?.(fresh);
      setData(d => d ? { ...d, user: fresh } : d);
      setNote("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update KYC status");
    } finally {
      setBusy(false);
    }
  };

  if (!user) return null;

  return (
    <Sheet open={!!user} onOpenChange={open => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-hidden p-0 flex flex-col" data-testid="account-360-drawer">
        <SheetCloseX/>
        <SheetHeader className="px-6 pt-6 pb-3 border-b border-border">
          <SheetTitle className="display text-2xl tracking-tight flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent"/>
            {user.name || user.email}
          </SheetTitle>
          <div className="text-[11.5px] text-foreground/55 mono">
            {user.email} · <span className="uppercase">{user.role}</span> · {user.company || "—"}
          </div>
        </SheetHeader>

        {/* Tab strip */}
        <div className="flex gap-1 px-4 pt-3 pb-2 border-b border-border overflow-x-auto" data-testid="account-360-tabs">
          {visibleTabs.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`account-360-tab-${t.id}`}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11.5px] border transition-all ${
                  active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-white text-foreground/65 border-border hover:border-foreground/40"
                }`}>
                <Icon className="w-3 h-3"/>{t.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading || !data ? (
            <div className="flex items-center justify-center py-16 text-foreground/55">
              <Loader2 className="w-4 h-4 animate-spin mr-2"/>Loading account…
            </div>
          ) : (
            <>
              {tab === "profile"   && <ProfileTab data={data} onReviewKyc={reviewKyc} busy={busy} note={note} setNote={setNote}/>}
              {tab === "business"  && <BusinessTab data={data}/>}
              {tab === "addresses" && <AddressesTab data={data}/>}
              {tab === "payments"  && <PaymentMethodsTab data={data}/>}
              {tab === "wallet"    && <WalletTab data={data}/>}
              {tab === "banking"   && <BankingTab data={data}/>}
              {tab === "revenue"   && <RevenueTab data={data}/>}
              {tab === "controls"  && <AdminUserControls data={data} onChange={() => {
                // Reload account-360 to reflect changes (suspended flag, etc.)
                api.get(`/admin/users/${data.user.id}/account-360`)
                  .then(r => setData(r.data))
                  .catch(() => {});
              }}/>}
              {tab === "activity"  && <ActivityTab userId={data.user.id}/>}
              {tab === "settings"  && <SellerSettingsTab data={data}/>}
              {tab === "domain"    && <DomainTab data={data} onChange={() => {
                api.get(`/admin/users/${data.user.id}/account-360`)
                  .then(r => setData(r.data))
                  .catch(() => {});
              }}/>}
              {tab === "team"      && <TeamTab data={data} onChange={() => {
                api.get(`/admin/users/${data.user.id}/account-360`)
                  .then(r => setData(r.data))
                  .catch(() => {});
              }}/>}
              {tab === "notifs"    && <NotificationsTab data={data}/>}
              {tab === "security"  && <SecurityTab data={data}/>}
              {tab === "login-diag" && <LoginDiagnosticsTab data={data}/>}
              {tab === "danger"    && <DangerZoneTab data={data} onDeleted={(uid) => {
                // Close the drawer + let the parent strip the row from the list.
                onDeleted?.(uid);
                onClose?.();
              }}/>}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
